import {Card, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {useLazyQuery} from '@apollo/client'
import {GET_POLICY_TYPE_DISTRIBUTION} from '../../../gql/queries/reportsQuery'

interface IPolicyTypeDistributionData {
  policyTypeName: string
  policiesCount: number
  percentage: number
}

interface IResPolicyTypeDistribution {
  policyTypeDistribution: IPolicyTypeDistributionData[]
}

interface IPolicyTypeDistributionChartProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  refreshTrigger?: number
}

export const PolicyTypeDistributionChart = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  refreshTrigger,
}: IPolicyTypeDistributionChartProps) => {
  const [getDistributionData, {data, loading}] = useLazyQuery<IResPolicyTypeDistribution>(
    GET_POLICY_TYPE_DISTRIBUTION,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    getDistributionData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getDistributionData])

  useEffect(() => {
    if (!data?.policyTypeDistribution || loading || parentLoading) return

    const distributionData = data.policyTypeDistribution

    const labels = distributionData.map((item) => item.policyTypeName)
    const series = distributionData.map((item) => item.policiesCount)
    const percentages = distributionData.map((item) => item.percentage)

    const options: ApexOptions = {
      series,
      chart: {
        type: 'donut',
        height: 350,
      },
      labels,
      legend: {
        position: 'right',
        fontSize: '12px',
        markers: {
          width: 8,
          height: 8,
        },
        formatter: (seriesName, opts) => {
          const percentage = percentages[opts.seriesIndex]
          const count = series[opts.seriesIndex]
          return `${seriesName} (${count})`
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val.toFixed(1)}%`,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const percentage = percentages[opts.seriesIndex]
            return `${val} policies (${percentage.toFixed(2)}%)`
          },
        },
      },
      colors: [
        '#3b82f6',
        '#ef4444',
        '#10b981',
        '#f59e0b',
        '#8b5cf6',
        '#ec4899',
        '#14b8a6',
        '#f97316',
        '#6366f1',
        '#84cc16',
      ],
      plotOptions: {
        pie: {
          donut: {
            size: '25%',
            labels: {
              show: false,
            },
          },
        },
      },
    }

    const chart = new ApexCharts(document.querySelector('#policy-type-distribution-chart'), options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [data, loading, parentLoading])

  return (
    <Card className='card-flush h-100'>
      <Card.Header className='border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Policies by Product Type (Top 10)</span>
          <span className='text-muted fw-semibold fs-7'>
            Distribution of policies grouped by product category
          </span>
        </h3>
      </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || parentLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && !parentLoading && (
          <div id='policy-type-distribution-chart' style={{minHeight: 350}}></div>
        )}
      </Card.Body>
    </Card>
  )
}
